<template>
  <b-modal
    id="modal-merchant-add"
    ref="modal"
    v-model="open"
    size="lg"
    no-close-on-backdrop
    hide-footer
    title="CREATE TICKET"
  >
    <b-overlay
      :show="modalLoading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <ValidationObserver
        ref="createMerchantForm"
        v-slot="{ handleSubmit }"
        slim
      >
        <b-form
          class="my-8 mx-2"
          @submit.prevent="handleSubmit(onClickCreateTicket)"
        >
          <b-row
            class="form-box"
            :class="{ 'duplicate-border': isTicketExist }"
          >
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                class="required"
                label="Waybill Number"
                label-for="h-waybill"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="waybill"
                  rules="required"
                >
                  <b-form-input
                    id="waybill"
                    v-model="form.waybill_number"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the waybill number"
                    type="text"
                    autocomplete="nope"
                    @input="debouncedFetchDataByWaybill"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="Ticket Category"
                label-for="h-ticketType"
                class="required"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="ticketType"
                  rules="required"
                >
                  <v-select
                    v-model="form.ticket_type_id"
                    :options="ticketTypes"
                    :reduce="option => option.id"
                    label="name"
                    autocomplete="nope"
                    placeholder="Select the ticket category"
                    class="bg-white"
                  >
                    <template v-slot:option="option">
                      {{ option.name }}
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <div
              v-if="isTicketExist"
              class="d-flex justify-content-center align-items-center w-100 px-1"
            >
              <b-badge
                variant="light-danger"
                class="duplicate-banner"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="20"
                  class="text-danger alert-icon"
                />
                <span style="font-weight: bold;">Duplicate Warning</span> : An active ticket already exists for this waybill (
                <router-link
                  :to="{ name: 'ticket-details', params: { id: ticketId } }"
                  style="font-weight: bold; text-decoration: underline; color: inherit;"
                >
                  Ref - {{ ticketId }}
                </router-link>)
              </b-badge>
            </div>
          </b-row>
          <b-row class="mt-1">
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                class="required"
                label="Merchant Name"
                label-for="h-merchant"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="merchant"
                  rules="required"
                >
                  <b-form-input
                    id="merchant"
                    v-model="form.merchant_name"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Merchant Name"
                    type="text"
                    :disabled="true"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                class="required"
                label="Reported By"
                label-for="h-reporderBy"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="reporderBy"
                  rules="required"
                >
                  <v-select
                    v-model="form.reported_by"
                    :options="reporterOptions"
                    :reduce="option => option.value"
                    label="name"
                    autocomplete="nope"
                    placeholder="Select the reported by"
                  >
                    <template v-slot:option="option">
                      {{ option.name }}
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="Name"
                label-for="h-reporterName"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="reporterName"
                  rules=""
                >
                  <b-form-input
                    id="reporterName"
                    v-model="form.reported_person_name"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the name"
                    autocomplete="nope"
                    :disabled="form.reported_by === 'merchant' || form.reported_by === null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="Phone Number"
                label-for="h-reporterPhone"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="reporterPhone"
                  rules=""
                >
                  <b-form-input
                    id="reporterPhone"
                    v-model="form.reported_person_phone"
                    :state="errors.length > 0 ? false:null"
                    type="number"
                    placeholder="Enter the phone number"
                    autocomplete="nope"
                    :disabled="form.reported_by === 'merchant' || form.reported_by === null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                class="required"
                label="Assign Ticket"
                label-for="h-assignee"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="assignee"
                  rules="required"
                >
                  <v-select
                    v-model="form.assignee"
                    :options="assigneeOptions"
                    :reduce="option => option.id"
                    label="first_name"
                    placeholder="Select assignee"
                  >
                    <template v-slot:option="option">
                      {{ option.reference_id }} | {{ option.first_name }} {{ option.last_name }}
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12">
              <b-form-group
                label="Subject"
                label-for="h-subject"
                class="required"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="h-subject"
                  vid="h-subject"
                  rules="required"
                >
                  <b-form-textarea
                    id="h-subject"
                    v-model="form.subject"
                    placeholder="Enter Subject"
                    class="subject-textarea"
                    :class="form.subject.length >= maxSubject ? 'text-danger' : ''"
                    :maxlength="maxSubject"
                    :state="form.subject.length < maxSubject"
                    style="overflow: auto; scrollbar-width: none;"
                    :rows="3"
                  />
                  <small
                    class="textarea-counter-value float-right"
                    :class="form.subject.length >= maxSubject ? 'bg-danger' : ''"
                  >
                    <span class="char-count">{{ form.subject.length }}</span> / {{ maxSubject }}
                  </small>
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12">
              <b-form-group
                label="Details Description"
                label-for="h-description"
                class="required"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="h-description"
                  vid="h-description"
                  rules="required"
                >
                  <b-form-textarea
                    id="h-description"
                    v-model="form.description"
                    placeholder="Enter the details description"
                    :class="form.description.length >= maxDescription ? 'text-danger' : ''"
                    :maxlength="maxDescription"
                    :state="form.description.length < maxDescription"
                    style="overflow: auto; scrollbar-width: none;"
                    :rows="3"
                  />
                  <small
                    class="textarea-counter-value float-right"
                    :class="form.description.length >= maxDescription ? 'bg-danger' : ''"
                  >
                    <span class="char-count">{{ form.description.length }}</span> / {{ maxDescription }}
                  </small>
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12">
              <b-form-group
                label="Attachments"
                label-for="h-name"
              >
                <UploadFile @files-selected="handleAttachments" />
              </b-form-group>
            </b-col>
          </b-row>
          <!-- submit and reset -->
          <b-row>
            <b-col
              cols="12"
              class="d-flex justify-content-end my-2"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                class="mr-1 custom-button-color"
              >
                Submit
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="reset"
                variant="outline-secondary"
                @click="onClearForm"
              >
                Clear
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </ValidationObserver>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BButton,
  BModal,
  BCol,
  BFormGroup,
  BForm,
  BFormInput,
  BFormTextarea,
  BRow,
  BOverlay,
  BBadge,
  VBModal,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import UploadFile from '../../components/Image-uploader/ImageUploader.vue'

const TicketRepository = RepositoryFactory.get('ticket')
const ResourceRepository = RepositoryFactory.get('resource')

function initialState() {
  return {
    open: false,
    id: null,
    form: {
      subject: '',
      description: '',
      waybill_number: '',
      merchant_name: '',
      merchant_id: '',
      reported_by: null,
      reported_person_name: '',
      reported_person_phone: '',
      ticket_type_id: null,
      assignee: null,
      attachments: [],
    },
    merchantAdminName: '',
    merchantAdminPhone: '',
    modalLoading: false,
    assigneeOptions: [],
    reporterOptions: [
      { name: 'Staff', value: 'staff' },
      { name: 'Merchant', value: 'merchant' },
      { name: 'Customer', value: 'customer' },
    ],
    search: '',
    maxSubject: 100,
    maxDescription: 750,
    isTicketExist: false,
    debounceTimeout: null,
    ticketTypes: [],
    ticketId: '',
  }
}

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    BButton,
    BModal,
    BCol,
    BFormGroup,
    BForm,
    BFormInput,
    BFormTextarea,
    BRow,
    BOverlay,
    BBadge,
    'v-select': vSelect,
    UploadFile,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return initialState()
  },
  watch: {
    'form.reported_by': 'onReportedByChange',
  },
  methods: {
    async openModal() {
      await Object.assign(this.$data, initialState())
      this.open = true
      this.getTicketTypes()
      this.getAssignee()
    },
    async getTicketTypes() {
      try {
        this.modalLoading = true
        this.ticketTypes = localStorage.getItem('ticket_types') ? JSON.parse(localStorage.getItem('ticket_types')) : (await TicketRepository.getTicketTypes()).data.data
        if (!localStorage.getItem('ticket_types')) localStorage.setItem('ticket_types', JSON.stringify(this.ticketTypes))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.modalLoading = false
    },
    debouncedFetchDataByWaybill() {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.fetchDataByWaybill()
      }, 2000)
    },

    async fetchDataByWaybill() {
      if (!this.form.waybill_number) {
        this.form.merchant_name = ''
        this.form.merchant_id = ''
        this.merchantAdminName = ''
        this.merchantAdminPhone = ''
        this.isTicketExist = false
        if (this.form.reported_by === 'merchant') {
          this.applyMerchantReporterDetails()
        }
        return
      }

      try {
        this.modalLoading = true
        const response = await TicketRepository.getDataByWaybill(this.form.waybill_number)
        const { data } = response.data

        this.isTicketExist = data.is_ticket_exist ? data.is_ticket_exist : false

        this.form.merchant_name = data.merchant_data.name
        this.form.merchant_id = data.merchant_data.id
        this.merchantAdminName = data.merchant_admin && data.merchant_admin.name ? data.merchant_admin.name : ''
        this.merchantAdminPhone = data.merchant_admin && data.merchant_admin.phone ? data.merchant_admin.phone : ''
        this.applyMerchantReporterDetails()

        if (this.isTicketExist) {
          this.ticketId = data.ticket_id
        }
      } catch (e) {
        this.convertAndNotifyError(e)
        this.form.merchant_name = ''
        this.form.merchant_id = ''
        this.merchantAdminName = ''
        this.merchantAdminPhone = ''
        this.isTicketExist = false
        if (this.form.reported_by === 'merchant') {
          this.applyMerchantReporterDetails()
        }
      } finally {
        this.modalLoading = false
      }
    },
    onReportedByChange(val) {
      if (val === 'merchant') {
        this.applyMerchantReporterDetails()
      } else {
        this.form.reported_person_name = ''
        this.form.reported_person_phone = ''
      }
    },
    applyMerchantReporterDetails() {
      if (this.form.reported_by !== 'merchant') {
        return
      }
      this.form.reported_person_name = this.merchantAdminName || ''
      this.form.reported_person_phone = this.merchantAdminPhone || ''
    },
    async getAssignee() {
      this.assigneeOptions = localStorage.getItem('assignee_list') ? JSON.parse(localStorage.getItem('assignee_list')) : (await ResourceRepository.getAllStaffUsersDropdown()).data.data
      if (!localStorage.getItem('assignee_list')) localStorage.setItem('assignee_list', JSON.stringify(this.assigneeOptions))
    },
    handleAttachments(selectedFiles) {
      this.form.attachments = selectedFiles
    },
    async onClickCreateTicket() {
      try {
        this.modalLoading = true
        const data = {
          waybill_number: this.form.waybill_number,
          ticket_type_id: this.form.ticket_type_id,
          merchant_id: this.form.merchant_id,
          reported_by: this.form.reported_by,
          reported_person_name: this.form.reported_person_name,
          reported_person_phone: this.form.reported_person_phone,
          assignee: this.form.assignee,
          subject: this.form.subject,
          message: this.form.description,
        }
        const formData = new FormData()
        Object.keys(data).forEach(key => {
          if (data[key] !== null && data[key] !== undefined) { // Also check for undefined
            formData.append(key, data[key])
          }
        })

        if (this.form.attachments && this.form.attachments.length > 0) {
          this.form.attachments.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file)
          })
        }

        const res = await TicketRepository.createTicket(formData)
        if (res.status === 201) {
          if (typeof res.data === 'string') {
            if (res.data.includes('Unknown: file')) {
              res.data = res.data.replace(/<\/?[^>]+(>|$)/g, '')
              res.data = JSON.parse(res.data.replace("Notice:  Unknown: file created in the system's temporary directory in Unknown on line 0", ''))
            }
          }
          this.showSuccessMessage('Ticket Added Successfully')
          this.open = false
          await this.$router.push(`/tickets/${res.data.data.id}`)
        } else {
          this.showErrorMessage("Couldn't add ticket")
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.modalLoading = false
    },
    onClearForm() {
      // Reset the form fields to their initial state
      Object.assign(this.form, {
        subject: '',
        description: '',
        waybill_number: '',
        merchant_name: '',
        merchant_id: '',
        reported_by: null,
        ticket_type_id: null, // Make sure to reset ticket type as well
        reported_person_name: '',
        reported_person_phone: '',
        assignee: null, // Reset assignee
        attachments: [], // Clear attachments
      })
      this.merchantAdminName = ''
      this.merchantAdminPhone = ''
      this.isTicketExist = false
      this.ticketId = '' // Clear ticketId as well
      // Optionally, reset validation state
      this.$nextTick(() => {
        if (this.$refs.createMerchantForm) {
          this.$refs.createMerchantForm.reset()
        }
      })
    },
  },
}
</script>
<style scoped>
@import "../../../node_modules/@syncfusion/ej2-base/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-vue-navigations/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-inputs/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-buttons/styles/fabric.css";
</style>
<style lang="scss">
@import '@core/scss/vue/required.scss';
</style>
<style lang="scss" scoped>
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
.sub-heading {
  text-align: center;
}
.upload-cancel {
  position: absolute;
  right: 81px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 10;
}
.position-relative {
  position: relative;
}
.form-box {
  background: #E2E8F0;   /* light gray */
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid #e5e7eb; /* subtle border */
}
.duplicate-border {
  border-color: red;
}
.subject-textarea {
  height: 40px;
}
.duplicate-banner {
  font-size: 0.9rem;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
}
.alert-icon {
  margin-right: 10px;
  width: 20px;
  height: 20px;
}
</style>
